import { z } from "zod/v4-mini";

import { ApiDataSourceSpec, apiDataSource } from "@/io/datasource/ApiDatasource.ts";
import {
  BulkCreateGroupedInclusionItem,
  BulkCreateGroupedInclusionResponse,
  BulkSubmitGroupedInclusionDraftsResponse,
  GroupedInclusionDraft,
  bulkCreateGroupedInclusionResponseSchema,
  bulkSubmitGroupedInclusionDraftsResponseSchema,
  groupedInclusionDraftSchema,
} from "@/models/GroupedInclusionModel.ts";
import {
  LibreViewClinicPatient,
  LibreViewClinicPatientsFilters,
  LibreViewIgnoreType,
  LibreViewStatusResponse,
  libreViewClinicPatientsResponseSchema,
  libreViewStatusResponseSchema,
} from "@/models/LibreViewModel.ts";
import { cz } from "@/utils/zodUtils.ts";

export class PatientSyncRepository {
  constructor(private readonly datasource: ApiDataSourceSpec = apiDataSource) {}

  getLibreViewStatus = (): Promise<LibreViewStatusResponse> =>
    this.datasource.get("practitioner/libreview/status/", libreViewStatusResponseSchema);

  getLibreViewClinicPatients = (filters?: LibreViewClinicPatientsFilters): Promise<LibreViewClinicPatient[]> =>
    this.datasource.get("practitioner/libreview/clinic-patients/", libreViewClinicPatientsResponseSchema, {
      params: {
        tir_max: filters?.tirMax,
        tbr_min: filters?.tbrMin,
        last_data_max_age_days: filters?.lastDataMaxAgeDays,
      },
    });

  ignoreLibreViewPatient = (email: string, ignoreType?: LibreViewIgnoreType): Promise<undefined> =>
    this.datasource.post("practitioner/libreview/ignore-patient/", cz.ignore(), {
      email,
      ...(ignoreType !== undefined ? { ignore_type: ignoreType } : {}),
    });

  bulkCreateGroupedInclusions = (
    patients: BulkCreateGroupedInclusionItem[],
  ): Promise<BulkCreateGroupedInclusionResponse> =>
    this.datasource.post("practitioner/grouped-inclusion/bulk-create/", bulkCreateGroupedInclusionResponseSchema, {
      patients: patients.map(p => ({
        first_name: p.firstName,
        last_name: p.lastName,
        email: p.email,
        phone_number: p.phoneNumber,
        libreview_id: p.libreviewId,
      })),
    });

  getGroupedInclusionDrafts = (): Promise<GroupedInclusionDraft[]> =>
    this.datasource.get("practitioner/grouped-inclusion/drafts/", cz.forcedArray(groupedInclusionDraftSchema));

  setGroupedInclusionPhoneNumber = (id: string, phoneNumber: string): Promise<undefined> =>
    this.datasource.post(`practitioner/grouped-inclusion/${id}/set-phone-number/`, cz.ignore(), {
      phone_number: phoneNumber,
    });

  bulkSubmitGroupedInclusionDrafts = (patientIds: string[]): Promise<BulkSubmitGroupedInclusionDraftsResponse> =>
    this.datasource.post(
      "practitioner/grouped-inclusion/bulk-submit-drafts/",
      bulkSubmitGroupedInclusionDraftsResponseSchema,
      { patient_ids: patientIds },
    );

  deleteGroupedInclusionDraft = (id: string): Promise<undefined> =>
    this.datasource.delete(`practitioner/grouped-inclusion/${id}/delete-draft/`, cz.ignore());

  exportGroupedInclusionDraftsXls = (): Promise<Blob> =>
    this.datasource.get("practitioner/grouped-inclusion/drafts-xlsx/", z.instanceof(Blob), {
      responseType: "blob",
    });
}
